"use client";

import { useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
export default function Localherotrust() {
  const container = useRef<HTMLElement>(null);
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add({
      reduced: "(prefers-reduced-motion: reduce)",
      normal: "(prefers-reduced-motion: no-preference)"
    }, context => {
      const conditions = context.conditions as {
        reduced: boolean;
        normal: boolean;
      };
      if (conditions.reduced) {
        gsap.set([".hero-hairline", ".hero-kicker", ".hero-caption", ".hero-line", ".hero-lead", ".hero-cta"], {
          opacity: 1,
          y: 0
        });
        gsap.set(".marker-path", {
          strokeDashoffset: 0
        });
        return;
      }
      gsap.from(".hero-hairline", {
        scaleX: 0,
        transformOrigin: "left center",
        duration: 0.7,
        ease: "power2.out",
        scrollTrigger: {
          trigger: container.current,
          start: "top 85%"
        }
      });
      gsap.from(".hero-kicker", {
        opacity: 0,
        y: 12,
        duration: 0.8,
        delay: 0.15,
        ease: "power3.out",
        scrollTrigger: {
          trigger: container.current,
          start: "top 85%"
        }
      });
      gsap.from(".hero-caption", {
        opacity: 0,
        y: 12,
        duration: 0.8,
        delay: 0.3,
        ease: "power3.out",
        scrollTrigger: {
          trigger: container.current,
          start: "top 85%"
        }
      });
      gsap.from(".hero-line", {
        yPercent: 110,
        opacity: 0,
        duration: 1.1,
        stagger: 0.09,
        ease: "cubic-bezier(0.22,1,0.36,1)",
        scrollTrigger: {
          trigger: container.current,
          start: "top 85%"
        }
      });
      const markerEl = container.current?.querySelector(".marker-path") as SVGPathElement | null;
      if (markerEl) {
        const length = markerEl.getTotalLength();
        gsap.set(markerEl, {
          strokeDasharray: length,
          strokeDashoffset: length
        });
        gsap.to(markerEl, {
          strokeDashoffset: 0,
          duration: 0.9,
          delay: 1.1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: container.current,
            start: "top 85%"
          }
        });
      }
      gsap.from(".hero-lead", {
        opacity: 0,
        y: 24,
        duration: 0.8,
        delay: 1.4,
        ease: "power3.out",
        scrollTrigger: {
          trigger: container.current,
          start: "top 85%"
        }
      });
      gsap.from(".hero-cta", {
        opacity: 0,
        y: 16,
        duration: 0.7,
        delay: 1.6,
        ease: "power3.out",
        scrollTrigger: {
          trigger: container.current,
          start: "top 85%"
        }
      });
    });
  }, {
    scope: container
  });
  return <section data-section-id="23002" id="localherotrust" data-nav="light" ref={container} className="relative bg-background text-foreground overflow-hidden" style={{
    paddingTop: "clamp(140px, 18vh, 220px)",
    paddingBottom: "clamp(140px, 18vh, 220px)"
  }}>
      <div className="mx-auto w-full max-w-[1360px]" style={{
      paddingLeft: "clamp(20px, 4vw, 56px)",
      paddingRight: "clamp(20px, 4vw, 56px)"
    }}>
        {}
        <div className="hero-hairline h-px w-full bg-border mb-10 md:mb-16" />

        <div className="grid grid-cols-1 md:grid-cols-12 gap-x-6 gap-y-10">
          {}
          <div className="md:col-span-3">
            <p className="hero-kicker font-sans text-xs font-medium uppercase text-muted-foreground" style={{
            letterSpacing: "0.18em"
          }}>
              My Approach
            </p>
            <p className="hero-caption mt-6 font-sans text-sm text-muted-foreground max-w-[220px] leading-relaxed">
              Direct access, clear results.
            </p>
          </div>

          {}
          <div className="md:col-span-9">
            <h1 className="font-serif font-normal text-foreground" style={{
            fontSize: "clamp(2.25rem, 4.5vw, 3.65rem)",
            letterSpacing: "-0.035em",
            lineHeight: "0.94"
          }}>
              <span className="block overflow-hidden">
                <span className="hero-line inline-block">Strategy,</span>
              </span>
              <span className="block overflow-hidden">
                <span className="hero-line inline-block">built on real&nbsp;</span>
              </span>
              <span className="block overflow-hidden">
                <span className="hero-line inline-block relative">
                  <span className="italic relative inline-block">
                    impact
                    {}
                    <svg aria-hidden="true" viewBox="0 0 420 40" preserveAspectRatio="none" className="absolute left-0 right-0 pointer-events-none" style={{
                    bottom: "-0.12em",
                    width: "100%",
                    height: "0.32em"
                  }}>
                      <path className="marker-path" d="M4,24 C60,10 130,32 200,18 C270,6 340,28 416,16" fill="none" stroke="hsl(220 80% 30%)" strokeWidth="10" strokeLinecap="round" style={{
                      mixBlendMode: "multiply",
                      opacity: 0.9
                    }} />
                    </svg>
                  </span>
                  <span>.</span>
                </span>
              </span>
            </h1>

            <p className="hero-lead mt-14 font-sans text-foreground/85 max-w-[640px]" style={{
            fontSize: "clamp(1.1875rem, 1.3vw, 1.4rem)",
            lineHeight: "1.55"
          }}>
              I provide hands-on SEO consultancy for businesses seeking sustainable growth. By working directly with me, you get a tailored strategy, transparent reporting, and practical implementation. I focus on honest measurement and clear deliverables, ensuring your search visibility evolves alongside modern AI-powered search experiences.
            </p>

            <div className="hero-cta mt-10">
              <Button asChild variant="outline" className="rounded-lg border border-primary bg-transparent text-primary hover:bg-primary hover:text-primary-foreground h-auto px-7 py-3.5 text-[1.0625rem] font-medium transition-colors duration-300">
                <Link to="/ContactPage">
                  Book a consultation.
                  <ArrowRight className="ml-1 h-4 w-4" strokeWidth={1.25} />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}